import { Typography } from "@mui/joy";

import { Card } from "primereact/card";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useSiteContext } from "../../../context/siteContext";
import styles from "./site-details.module.css";
import { Input } from "../../../components/Shared/Forms";
import { PhoneInput } from "react-international-phone";
import { Button } from "primereact/button";
import ButtonPanel from "../../../components/Shared/ButtonPanel";
import useApiService from "../../../hooks/useApiService";
import { Site } from "../../../models/site.interface";
import { useToast } from "../../../context/ToastContext";
import { capitaliseFirstLetter, snakeToHuman } from "../../../utils/generalUtils";

type formDataType = {
  client_name: string;
  client_agent_name: string;
  client_agent_phone: string;
  client_agent_email: string;
};

export default function SiteClient() {
  const { site, setSite } = useSiteContext();
  const toast = useToast();
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [somethingChanged, setSomethingChanged] = useState<boolean>(false);
  const [formData, setFormData] = useState<formDataType>({
    client_name: "",
    client_agent_name: "",
    client_agent_phone: "",
    client_agent_email: "",
  });

  const requiredFields = ["client_name"];

  const { loading: updatingSite, fetchData: updateSite } = useApiService<Site>({
    fetchOnMount: false,
    url: `/site-list/${site?.id}`,
    method: "PUT",
    onSuccess: data => {
      toast("success", "Site updated successfully.");
      setSite(data);
      setSomethingChanged(false);
    },
  });

  useEffect(() => {
    if (site) {
      setFormData({
        client_name: site.client_name || "",
        client_agent_name: site.client_agent_name || "",
        client_agent_phone: site.client_agent_phone || "",
        client_agent_email: site.client_agent_email || "",
      });
    }
  }, [site]);

  function cancelChanges() {
    setFormData({
      client_name: site?.client_name || "",
      client_agent_name: site?.client_agent_name || "",
      client_agent_phone: site?.client_agent_phone || "",
      client_agent_email: site?.client_agent_email || "",
    });
    setSomethingChanged(false);
    setFieldErrors({});
  }

  async function handleSubmit() {
    // validate fields and set errors
    const errors: { [key: string]: string } = {};

    requiredFields.forEach(field => {
      if (!formData[field as keyof formDataType] || formData[field as keyof formDataType] === "") {
        errors[field] = `${capitaliseFirstLetter(snakeToHuman(field))} is required`;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    const sitePayload = {
      ...site,
      ...formData,
    };

    delete sitePayload.industry_list;
    delete sitePayload.job_task_list;
    delete sitePayload.setup_complete;
    delete sitePayload.badge;
    delete sitePayload.updated_at;
    delete sitePayload.created_at;
    delete sitePayload.deleted_at;

    await updateSite({
      payload: sitePayload,
    });
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    // Clear any existing error for this field
    if (fieldErrors[name]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    setSomethingChanged(true);

    setFormData({
      ...formData,
      [name]: value,
    });
  }

  return (
    <>
      <Typography level="h2" component="h1">
        Client: {site?.name}
      </Typography>
      <Card>
        <div className={styles["responsive-grid-2"]}>
          <Input
            name={"client_name"}
            label={"Client name"}
            required={true}
            value={formData.client_name}
            onChange={handleInputChange}
            error={fieldErrors?.client_name}
          />
          <Input
            name={"client_agent_name"}
            label={"Agent name"}
            value={formData.client_agent_name}
            onChange={handleInputChange}
            error={fieldErrors?.client_agent_name}
          />
          <div>
            <label className="block mb-2">Phone number</label>
            <PhoneInput
              defaultCountry="za"
              value={formData.client_agent_phone}
              name={"client_agent_phone"}
              onChange={e => {
                setFormData({
                  ...formData,
                  client_agent_phone: e || "",
                });
              }}
              inputStyle={{
                width: "100%",
                height: "50px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                padding: "0.75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
                backgroundColor: "#ffffff",
                outline: "none",
                transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #d1d5db",
                  borderRadius: "6px 0 0 6px",
                  backgroundColor: "#ffffff",
                  padding: "0.75rem 0.5rem",
                  height: "50px",
                },
              }}
              inputClassName="p-inputtext"
            />
          </div>
          <Input
            name={"client_agent_email"}
            label={"Agent email address"}
            value={formData.client_agent_email}
            onChange={handleInputChange}
            placeholder={"Example: john.doe@dsf.co.za"}
            type={"email"}
            error={fieldErrors?.client_agent_email}
          />
        </div>
      </Card>
      <ButtonPanel inModal={false}>
        <Button
          label={"Cancel"}
          outlined={true}
          disabled={!somethingChanged || updatingSite}
          onClick={cancelChanges}
        />
        <Button
          label={"Save"}
          loading={updatingSite}
          disabled={updatingSite || !somethingChanged}
          onClick={async () => {
            await handleSubmit();
          }}
        />
      </ButtonPanel>
    </>
  );
}
